import React, { useState } from 'react';
import { Save, Settings, Shield, Database } from 'lucide-react';

const AdminSettings = () => {
  const [settings, setSettings] = useState({
    systemName: 'MeuFiel',
    maintenanceMode: false,
    allowNewRegistrations: true,
    maxPointsPerTransaction: 1000,
    emailNotifications: true,
    backupFrequency: 'daily',
    sessionTimeoutMinutes: 30,
    enableSessionTimeout: true
  });

  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <Settings className="mr-3 h-8 w-8 text-blue-600" />
          Configurações do Sistema
        </h1>
        <p className="mt-2 text-gray-600">
          Gerencie as configurações globais do sistema MeuFiel
        </p>
      </div>

      <div className="space-y-8">
        {/* General Settings */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Shield className="mr-2 h-5 w-5 text-blue-600" />
            Configurações Gerais
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nome do Sistema
              </label>
              <input
                type="text"
                value={settings.systemName}
                onChange={(e) => handleChange('systemName', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Máximo de Pontos por Transação
              </label>
              <input
                type="number"
                value={settings.maxPointsPerTransaction}
                onChange={(e) => handleChange('maxPointsPerTransaction', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Frequência de Backup
              </label>
              <select
                value={settings.backupFrequency}
                onChange={(e) => handleChange('backupFrequency', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="hourly">A cada hora</option>
                <option value="daily">Diário</option>
                <option value="weekly">Semanal</option>
                <option value="monthly">Mensal</option>
              </select>
            </div>
          </div>

          <div className="mt-6 space-y-4">
            <div className="flex items-center">
              <input
                id="maintenance"
                type="checkbox"
                checked={settings.maintenanceMode}
                onChange={(e) => handleChange('maintenanceMode', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="maintenance" className="ml-2 block text-sm text-gray-700">
                Modo de Manutenção
              </label>
            </div>

            <div className="flex items-center">
              <input
                id="registrations"
                type="checkbox"
                checked={settings.allowNewRegistrations}
                onChange={(e) => handleChange('allowNewRegistrations', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="registrations" className="ml-2 block text-sm text-gray-700">
                Permitir novos cadastros
              </label>
            </div>

            <div className="flex items-center">
              <input
                id="notifications"
                type="checkbox"
                checked={settings.emailNotifications}
                onChange={(e) => handleChange('emailNotifications', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="notifications" className="ml-2 block text-sm text-gray-700">
                Notificações por email
              </label>
            </div>
          </div>
        </div>

        {/* Security Settings */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Shield className="mr-2 h-5 w-5 text-blue-600" />
            Configurações de Segurança
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Timeout de Sessão (minutos)
              </label>
              <input
                type="number"
                min="5"
                max="480"
                value={settings.sessionTimeoutMinutes}
                onChange={(e) => handleChange('sessionTimeoutMinutes', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={!settings.enableSessionTimeout}
              />
              <p className="text-xs text-gray-500 mt-1">
                Tempo de inatividade antes do logout automático (5-480 min)
              </p>
            </div>
          </div>

          <div className="mt-6 space-y-4">
            <div className="flex items-center">
              <input
                id="sessionTimeout"
                type="checkbox"
                checked={settings.enableSessionTimeout}
                onChange={(e) => handleChange('enableSessionTimeout', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="sessionTimeout" className="ml-2 block text-sm text-gray-700">
                Habilitar timeout de sessão por inatividade
              </label>
            </div>
          </div>
        </div>

        {/* System Information */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Database className="mr-2 h-5 w-5 text-blue-600" />
            Informações do Sistema
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="font-medium text-gray-700">Versão</div>
              <div className="text-gray-900">1.0.0</div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="font-medium text-gray-700">Último Backup</div>
              <div className="text-gray-900">Hoje às 03:00</div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="font-medium text-gray-700">Status</div>
              <div className="text-green-600 font-medium">Online</div>
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="mt-8 flex justify-end">
        <button
          onClick={handleSave}
          disabled={loading}
          className={`px-6 py-2 rounded-md text-white font-medium flex items-center ${
            loading 
              ? 'bg-gray-400 cursor-not-allowed' 
              : saved 
                ? 'bg-green-600 hover:bg-green-700' 
                : 'bg-blue-600 hover:bg-blue-700'
          } transition-colors`}
        >
          <Save className="mr-2 h-4 w-4" />
          {loading ? 'Salvando...' : saved ? 'Salvo!' : 'Salvar Configurações'}
        </button>
      </div>
    </div>
  );
};

export default AdminSettings;
